import React from 'react';
import { cn } from '../../utils/cn';
import { getPasswordStrength } from '../../utils/accountValidation';

export interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
  showLabel?: boolean;
  showRequirements?: boolean;
}

/**
 * Password strength indicator component
 * Shows visual feedback for password strength and requirements
 */
export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  className,
  showLabel = true,
  showRequirements = true
}) => {
  const strength = getPasswordStrength(password);
  
  // Don't show anything for empty passwords
  if (!password) return null;

  // Calculate progress percentage
  const progressPercentage = (strength.score / 4) * 100;

  // Get progress bar color based on strength
  const getProgressColor = () => {
    switch (strength.score) {
      case 0:
      case 1:
        return 'bg-red-500';
      case 2:
        return 'bg-yellow-500';
      case 3:
        return 'bg-blue-500';
      case 4:
        return 'bg-green-500';
      default:
        return 'bg-gray-300';
    }
  };

  // Check individual requirements
  const requirements = [
    { label: 'At least 6 characters', met: password.length >= 6 },
    { label: 'Contains uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'Contains lowercase letter', met: /[a-z]/.test(password) },
    { label: 'Contains number', met: /\d/.test(password) }
  ];

  return (
    <div className={cn('space-y-2 sm:space-y-3', className)}>
      {/* Strength Bar and Label */}
      <div className="space-y-1 sm:space-y-2">
        {/* Progress Bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 sm:h-2.5">
          <div
            className={cn(
              'h-2 sm:h-2.5 rounded-full transition-all duration-300 ease-theme',
              getProgressColor()
            )}
            style={{ width: `${progressPercentage}%` }}
            role="progressbar"
            aria-valuenow={strength.score}
            aria-valuemin={0}
            aria-valuemax={4}
            aria-label={`Password strength: ${strength.label}`}
          />
        </div>

        {/* Strength Label */}
        {showLabel && (
          <div className="flex justify-between items-center">
            <span className="text-xs sm:text-sm text-muted-foreground">
              Password strength:
            </span>
            <span className={cn('text-xs sm:text-sm font-medium', strength.color)}>
              {strength.label}
            </span>
          </div>
        )}
      </div>

      {/* Requirements Checklist */}
      {showRequirements && (
        <div className="space-y-1 sm:space-y-2">
          <span className="text-xs sm:text-sm text-muted-foreground">
            Requirements:
          </span>
          <ul className="space-y-1 sm:space-y-1.5">
            {requirements.map((req, index) => (
              <li
                key={index}
                className={cn(
                  'flex items-center gap-2 text-xs sm:text-sm',
                  req.met ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
                )}
              >
                <span className={cn(
                  'w-3 h-3 sm:w-4 sm:h-4 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold flex-shrink-0',
                  req.met 
                    ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' 
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                )}>
                  {req.met ? '✓' : '○'}
                </span>
                <span className="leading-tight">{req.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};